import * as React from 'react';
import { cn } from '../../lib/utils';

const DropdownMenuContext = React.createContext({ open: false, setOpen: () => {} });

function DropdownMenu({ children }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen }}>
      <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
        {children}
      </div>
    </DropdownMenuContext.Provider>
  );
}

function DropdownMenuTrigger({ asChild, children, ...props }) {
  const { setOpen } = React.useContext(DropdownMenuContext);
  const handleClick = (e) => {
    e.stopPropagation();
    setOpen((o) => !o);
    if (props.onClick) props.onClick(e);
  };
  if (asChild && React.Children.count(children) === 1) {
    return React.cloneElement(React.Children.only(children), { onClick: handleClick });
  }
  return <button onClick={handleClick} {...props}>{children}</button>;
}

function DropdownMenuContent({ className, align = 'start', children, ...props }) {
  const { open } = React.useContext(DropdownMenuContext);
  if (!open) return null;
  return (
    <div
      className={cn(
        'absolute z-50 min-w-[8rem] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md',
        align === 'end' ? 'right-0' : 'left-0',
        'top-full mt-1',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function DropdownMenuItem({ className, onClick, children, ...props }) {
  const { setOpen } = React.useContext(DropdownMenuContext);
  return (
    <div
      role="menuitem"
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground',
        props.disabled && 'pointer-events-none opacity-50',
        className
      )}
      onClick={(e) => { if (onClick) onClick(e); setOpen(false); }}
      {...props}
    >
      {children}
    </div>
  );
}

function DropdownMenuLabel({ className, children, ...props }) {
  return (
    <div className={cn('px-2 py-1.5 text-sm font-semibold', className)} {...props}>
      {children}
    </div>
  );
}

function DropdownMenuSeparator({ className, ...props }) {
  return <div className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />;
}

function DropdownMenuGroup({ children }) {
  return <div>{children}</div>;
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
};
